import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import Logo from "../../../images/whitelogo.png";
import { motion, useInView } from "framer-motion";
import { SiFacebook, SiInstagram, SiLinkedin, SiTiktok } from "react-icons/si";

import { actionType } from "../../../context/reducer";
import { useStateValue } from "../../../context/StateProvider.js";
import content from "../../../language/index.js";

const Header = () => {
  const [isEnglish, setIsEnglish] = useState(false);
  const [{ language }, dispatch] = useStateValue();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeRoute, setActiveRoute] = useState("home");
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const lastScrollY = useRef(0);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const atTop = currentScrollY <= 80;

      if (atTop) {
        setIsHeaderVisible(true);
      } else if (currentScrollY > lastScrollY.current) {
        setIsHeaderVisible(false); // scrolling down
      } else {
        setIsHeaderVisible(true); // scrolling up
      }

      lastScrollY.current = currentScrollY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (!location.hash) {
      const matchedItem = navigationItems.find(
        (item) => item.route === location.pathname,
      );
      if (matchedItem) {
        setActiveRoute(matchedItem.key);
      } else {
        setActiveRoute(null); // No active nav for hash-only routes
      }
    } else {
      setActiveRoute(null); // Disable active state on anchor scrolls
    }
  }, [location]);

  const toggleLanguage = () => {
    const newLangIndex = isEnglish ? 1 : 0;
    setIsEnglish(!isEnglish);
    dispatch({ type: actionType.LANGUAGE, payload: newLangIndex });
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleRouteClick = (routeKey) => {
    setActiveRoute(routeKey);
    setIsMobileMenuOpen(false); // Close mobile menu when route is clicked
  };

  const navigationItems = [
    { key: "home", label: content[language].home, route: "/" },
    {
      key: "logistics",
      label: content[language].logistics,
      route: "/logistic-service",
    },
    {
      key: "staffing",
      label: content[language].staffing,
      route: "/staff-service",
    },
    {
      key: "cleaning",
      label: content[language].cleaning,
      route: "/cleaning-service",
    },
    {
      key: "security",
      label: content[language].security,
      route: "/security-service",
    },
    {
      key: "candidates",
      label: content[language].candidates,
      route: "/candidate-service",
    },
  ];

  const socialVariants = {
    hidden: { scale: 0, rotate: -180 },
    visible: {
      scale: 1,
      rotate: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut",
        delay: 0.3,
      },
    },
  };

  return (
    <>
      {/* Top Bar - scrolls with the page, not sticky */}
      <div className="bg-themeColor shadow-lg shadow-black/20">
        <header className="relative w-[95%] xl:w-[90%] mx-auto py-3">
          <div className="flex justify-between items-center h-14 md:h-14 4xl:h-28">
            {/* Mobile Menu Button */}
            <div className="lg:hidden">
              <button
                onClick={toggleMobileMenu}
                className="p-2 rounded-md text-white hover:text-themeColor hover:bg-white transition-colors duration-200"
              >
                {isMobileMenuOpen ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-6 h-6"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-6 h-6"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="3" y1="12" x2="21" y2="12"></line>
                    <line x1="3" y1="6" x2="21" y2="6"></line>
                    <line x1="3" y1="18" x2="21" y2="18"></line>
                  </svg>
                )}
              </button>
            </div>
            {/* Logo */}
            <Link to={"/"}>
              <img
                loading="lazy"
                src={Logo}
                className="h-10 md:h-10 4xl:h-20 5xl:h-28 w-auto object-contain"
                alt="logo"
              />
            </Link>
            {/* Language Toggle */}
            <div
              onClick={toggleLanguage}
              className="relative w-16 h-9 bg-themeColor lg:hidden rounded-full cursor-pointer p-1 flex items-center transition-all duration-300 hover:shadow-lg"
            >
              {/* Toggle circle */}
              <div
                className={`absolute top-1 w-7 h-7 bg-white rounded-full shadow-md transform transition-all duration-300 ease-in-out ${
                  isEnglish ? "translate-x-0" : "translate-x-7"
                }`}
              ></div>

              {/* Labels */}
              <div className="w-full flex justify-between px-1 text-sm PoppinsRegular relative z-10">
                <span
                  className={`transition-colors duration-300 ${
                    isEnglish ? "text-themeColor" : "text-white"
                  }`}
                >
                  EN
                </span>
                <span
                  className={`transition-colors duration-300 ${
                    !isEnglish ? "text-themeColor pr-1" : "text-white"
                  }`}
                >
                  FI
                </span>
              </div>
            </div>

            {/* Desktop Right Section */}
            <div className="hidden lg:flex items-center space-x-5">
              {/* Login */}
              <Link to={"/login"}>
                <p className="text-white/90 hover:text-white text-base 4xl:text-2xl 5xl:text-3xl PoppinsRegular transition-colors duration-200 cursor-pointer">
                  {content[language].LogIn}
                </p>
              </Link>

              <span className="h-5 w-px bg-white/15" aria-hidden="true"></span>

              {/* Language Toggle */}
              <div
                onClick={toggleLanguage}
                className="relative w-[60px] h-9 4xl:w-[80px] 4xl:h-12 rounded-full cursor-pointer p-1 flex items-center border border-white/15 transition-all duration-300 hover:border-white/30"
              >
                {/* Toggle circle */}
                <div
                  className={`absolute top-1 w-7 h-7 4xl:w-10 4xl:h-10 bg-white rounded-full shadow-md transform transition-all duration-300 ease-in-out ${
                    isEnglish ? "translate-x-0" : "translate-x-7"
                  }`}
                ></div>

                {/* Labels */}
                <div className="w-full flex justify-between px-1 text-sm 4xl:text-lg PoppinsRegular relative z-10">
                  <span
                    className={`transition-colors duration-300 ${
                      isEnglish ? "text-themeColor" : "text-white"
                    }`}
                  >
                    EN
                  </span>
                  <span
                    className={`transition-colors duration-300 ${
                      !isEnglish ? "text-themeColor pr-1" : "text-white"
                    }`}
                  >
                    FI
                  </span>
                </div>
              </div>

              <span className="h-5 w-px bg-white/15" aria-hidden="true"></span>

              {/* Social Icons */}
              <div>
                <motion.div
                  variants={socialVariants}
                  className="flex flex-row items-center space-x-3"
                >
                  <motion.a
                    href="https://www.linkedin.com/company/105676145/admin/dashboard/"
                    target="_blank"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    className="w-7 h-7 4xl:w-11 4xl:h-11 rounded-lg bg-[#0A66C2] flex items-center justify-center transition-transform duration-300"
                  >
                    <SiLinkedin className="w-4 h-4 4xl:w-6 4xl:h-6 text-white" />
                  </motion.a>
                  <motion.a
                    href="https://www.instagram.com/raftekgroup/?igsh=cWttODd5djhjdWs0&utm_source=qr#"
                    target="_blank"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    style={{
                      background:
                        "radial-gradient(circle at 30% 107%, #fdf497 0%, #fdf497 5%, #fd5949 45%, #d6249f 60%, #285AEB 90%)",
                    }}
                    className="w-7 h-7 4xl:w-11 4xl:h-11 rounded-lg flex items-center justify-center transition-transform duration-300"
                  >
                    <SiInstagram className="w-4 h-4 4xl:w-6 4xl:h-6 text-white" />
                  </motion.a>
                  <motion.a
                    href="https://www.facebook.com/profile.php?id=61575406075595&mibextid=wwXIfr&rdid=1DaeWcaABdMkLxxY#"
                    target="_blank"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    className="w-7 h-7 4xl:w-11 4xl:h-11 rounded-lg bg-[#1877F2] flex items-center justify-center transition-transform duration-300"
                  >
                    <SiFacebook className="w-4 h-4 4xl:w-6 4xl:h-6 text-white" />
                  </motion.a>
                  <motion.a
                    href="https://www.tiktok.com/@raftek.group?_t=ZN-8wIhf6Qmx0A&_r=1"
                    target="_blank"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    className="w-7 h-7 4xl:w-11 4xl:h-11 rounded-lg bg-black flex items-center justify-center transition-transform duration-300"
                  >
                    <SiTiktok className="w-4 h-4 4xl:w-6 4xl:h-6 text-white" />
                  </motion.a>
                </motion.div>
              </div>
            </div>
          </div>
        </header>
      </div>

      {/* Navigation Bar - sticky, hides on scroll down / reveals on scroll up */}
      <div
        className={`hidden lg:block sticky top-0 z-50 pt-3 pb-3 transition-transform duration-300 ease-in-out ${
          isHeaderVisible ? "translate-y-0" : "-translate-y-full"
        }`}
      >
        <div className="w-[95%] xl:w-[90%] mx-auto bg-black/60 backdrop-blur-xl border border-white/10 shadow-md shadow-black/30 rounded-full">
          <div className="pr-4">
            <div className="flex items-center justify-between py-2.5 4xl:py-4 ml-[3%]">
              <nav className="flex items-center space-x-1 xl:space-x-2">
                {navigationItems.map((item) => (
                  <Link
                    key={item.key}
                    to={item.route}
                    onClick={() => handleRouteClick(item.key)}
                    className={`PoppinsRegular transition-all duration-200 cursor-pointer rounded-full px-4 py-2 lg:text-sm xl:text-base 4xl:text-xl 5xl:text-2xl ${
                      activeRoute === item.key
                        ? "bg-white/10 text-white"
                        : "text-white/75 hover:text-white hover:bg-white/5"
                    }`}
                  >
                    {item.label}
                  </Link>
                ))}
              </nav>
              <Link
                className=""
                to={"/contact-us"}
                onClick={() => {
                  setActiveRoute("/contact-us");
                }}
              >
                <button className="bg-white text-themeColor rounded-full PoppinsRegular px-4 4xl:px-4 py-2 4xl:py-3 hover:bg-themegray transition-all duration-200 hover:shadow-lg 4xl:text-2xl 5xl:text-3xl">
                  {content[language].contact}
                </button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden fixed inset-0 z-50 transition-all duration-300 ${
          isMobileMenuOpen ? "opacity-100 visible" : "opacity-0 invisible"
        }`}
      >
        {/* Backdrop */}
        <div
          className={`absolute inset-0 bg-black transition-opacity duration-300 ${
            isMobileMenuOpen ? "opacity-50" : "opacity-0"
          }`}
          onClick={toggleMobileMenu}
        ></div>

        {/* Menu Panel - Slides from top */}
        <div
          className={`absolute top-0 left-0 right-0  bg-themeColor shadow-2xl transform transition-all duration-500 ease-out ${
            isMobileMenuOpen
              ? "translate-y-0 opacity-100"
              : "-translate-y-full opacity-0"
          }`}
        >
          <div className="p-6 max-h-screen overflow-y-auto">
            {/* Close Button */}
            <div className="flex justify-between items-center mb-8 pb-4 border-b border-bordercolor">
              <button
                onClick={toggleMobileMenu}
                className="p-2 rounded-full text-white hover:bg-white  hover:text-themeColor transition-all duration-200 hover:rotate-90"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-6 h-6"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
              <Link to={"/"}>
                <img
                  loading="lazy"
                  src={Logo}
                  className="h-12 md:h-12 w-auto object-contain"
                  alt="logo"
                />
              </Link>
              {/* Language Toggle */}
              <div
                onClick={toggleLanguage}
                className="relative w-[65px] h-9 bg-themeColor lg:hidden rounded-full cursor-pointer p-1 flex items-center transition-all duration-300 hover:shadow-lg"
              >
                {/* Toggle circle */}
                <div
                  className={`absolute top-1 w-7 h-7 bg-white rounded-full shadow-md transform transition-all duration-300 ease-in-out ${
                    isEnglish ? "translate-x-0" : "translate-x-7"
                  }`}
                ></div>

                {/* Labels */}
                <div className="w-full flex justify-between px-1 text-sm PoppinsRegular relative z-10">
                  <span
                    className={`transition-colors duration-300 ${
                      isEnglish ? "text-themeColor" : "text-white"
                    }`}
                  >
                    EN
                  </span>
                  <span
                    className={`transition-colors duration-300 ${
                      !isEnglish ? "text-themeColor mr-1" : "text-white"
                    }`}
                  >
                    FI
                  </span>
                </div>
              </div>
            </div>

            {/* Mobile Navigation */}
            <nav className="space-y-4 mb-8">
              {navigationItems.map((item, index) => (
                <div
                  key={item.key}
                  className={`transform transition-all duration-300 ${
                    isMobileMenuOpen
                      ? "translate-y-0 opacity-100"
                      : "translate-y-4 opacity-0"
                  }`}
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  <Link
                    to={item.route}
                    onClick={() => handleRouteClick(item.key)}
                    className={`block text-lg PoppinsRegular transition-all duration-200 py-4 px-1 mx-4 rounded-xl cursor-pointer ${
                      activeRoute === item.key
                        ? "text-white bg-themeColor shadow-lg transform scale-105 px-4 hover:text-themeColor hover:bg-white"
                        : "text-white hover:text-themeColor hover:bg-white hover:shadow-md"
                    }`}
                  >
                    <div className="flex items-center justify-between  ">
                      <span>{item.label}</span>
                      {activeRoute === item.key && (
                        <span className="inline-block w-2 h-2 bg-themegreen rounded-full animate-pulse"></span>
                      )}
                    </div>
                  </Link>
                </div>
              ))}
            </nav>

            {/* Mobile Actions */}
            <div
              className={`space-y-6 pt-6 border-t border-bordercolor transform transition-all duration-300 ${
                isMobileMenuOpen
                  ? "translate-y-0 opacity-100"
                  : "translate-y-4 opacity-0"
              }`}
              style={{ transitionDelay: "600ms" }}
            >
              {/* Login */}
              <Link
                to={"/login"}
                onClick={() => {
                  setIsMobileMenuOpen(false);
                }}
              >
                <button className="w-full bg-themeColor text-white PoppinsRegular px-6 py-4 transition-all duration-200 hover:scale-105 hover:shadow-xl transform rounded-full mb-4">
                  {content[language].LogIn}
                </button>
              </Link>

              {/* Contact Button */}
              <Link
                to={"/contact-us"}
                onClick={() => {
                  setActiveRoute("/contact-us");
                  setIsMobileMenuOpen(false);
                }}
              >
                <button className="w-full bg-themeColor text-white PoppinsRegular px-6 py-4 transition-all duration-200 hover:scale-105 hover:shadow-xl transform rounded-full">
                  {content[language].contact}
                </button>
              </Link>

              <div className="flex justify-center items-center space-x-3">
                <motion.a
                  href="https://www.linkedin.com/company/105676145/admin/dashboard/"
                  target="_blank"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-9 h-9 4xl:w-14 4xl:h-14 rounded-lg bg-[#0A66C2] flex items-center justify-center transition-transform duration-300"
                >
                  <SiLinkedin className="w-5 h-5 4xl:w-8 4xl:h-8 text-white" />
                </motion.a>
                <motion.a
                  href="https://www.instagram.com/raftekgroup/?igsh=cWttODd5djhjdWs0&utm_source=qr#"
                  target="_blank"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  style={{
                    background:
                      "radial-gradient(circle at 30% 107%, #fdf497 0%, #fdf497 5%, #fd5949 45%, #d6249f 60%, #285AEB 90%)",
                  }}
                  className="w-9 h-9 4xl:w-14 4xl:h-14 rounded-lg flex items-center justify-center transition-transform duration-300"
                >
                  <SiInstagram className="w-5 h-5 4xl:w-8 4xl:h-8 text-white" />
                </motion.a>
                <motion.a
                  href="https://www.facebook.com/profile.php?id=61575406075595&mibextid=wwXIfr&rdid=1DaeWcaABdMkLxxY#"
                  target="_blank"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-9 h-9 4xl:w-14 4xl:h-14 rounded-lg bg-[#1877F2] flex items-center justify-center transition-transform duration-300"
                >
                  <SiFacebook className="w-5 h-5 4xl:w-8 4xl:h-8 text-white" />
                </motion.a>
                <motion.a
                  href="https://www.tiktok.com/@raftek.group?_t=ZN-8wIhf6Qmx0A&_r=1"
                  target="_blank"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-9 h-9 4xl:w-14 4xl:h-14 rounded-lg bg-black flex items-center justify-center transition-transform duration-300"
                >
                  <SiTiktok className="w-5 h-5 4xl:w-8 4xl:h-8 text-white" />
                </motion.a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
